import React, { useState, useEffect } from 'react'
import { 
  Shield, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  AlertTriangle, 
  Info, 
  XCircle, 
  Clock,
  User,
  Package,
  Settings,
  CreditCard,
  Share2,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import { getAuditLogs, getAuditStats, AuditLogFilter } from '../../lib/auditLog'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface AuditLogWithDetails {
  id: string
  event_type: string
  user_id?: string
  admin_user_id?: string
  order_id?: string
  transfer_token?: string
  old_value?: string
  new_value?: string
  ip_address?: string
  user_agent?: string
  metadata?: Record<string, any>
  severity: 'info' | 'warning' | 'error' | 'critical'
  message: string
  created_at: string
  
  // Joined data
  secret_users?: {
    first_name: string
    last_name: string
    email?: string
    phone?: string
  }
  secret_admin_users?: {
    secret_users: {
      first_name: string
      last_name: string
      email: string
    }
  }
  secret_orders?: {
    order_number: string
    total_amount: number
  }
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogWithDetails[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())
  
  const [filter, setFilter] = useState<AuditLogFilter>({
    limit: 50,
    offset: 0
  })
  
  const [searchTerm, setSearchTerm] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState<'today' | '7d' | '30d' | '90d'>('30d')

  useEffect(() => {
    loadData()
  }, [filter, eventTypeFilter, severityFilter, dateRange])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Build filter
      const auditFilter: AuditLogFilter = {
        ...filter,
        eventType: eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        startDate: getDateFromRange(dateRange),
        endDate: new Date()
      }

      // Load logs and stats in parallel
      const [logsResult, statsResult] = await Promise.allSettled([
        getAuditLogs(auditFilter),
        getAuditStats(30)
      ])

      if (logsResult.status === 'fulfilled' && !logsResult.value.error) {
        let filteredLogs = logsResult.value.data
        
        // Apply search filter client-side
        if (searchTerm) {
          const search = searchTerm.toLowerCase()
          filteredLogs = filteredLogs.filter(log => 
            log.message.toLowerCase().includes(search) ||
            log.event_type.toLowerCase().includes(search) ||
            log.secret_orders?.order_number.toLowerCase().includes(search) ||
            log.secret_users?.email?.toLowerCase().includes(search) ||
            log.ip_address?.toLowerCase().includes(search)
          )
        }
        
        setLogs(filteredLogs)
      }

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value)
      }
      
    } catch (error) {
      console.error('Failed to load audit logs:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type='error'
          title='Loading Failed'
          message='Failed to load audit logs. Please try again.'
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  const getDateFromRange = (range: string): Date => {
    const now = new Date()
    switch (range) {
      case 'today':
        now.setHours(0, 0, 0, 0)
        return now
      case '7d':
        now.setDate(now.getDate() - 7)
        return now
      case '30d':
        now.setDate(now.getDate() - 30)
        return now
      case '90d':
        now.setDate(now.getDate() - 90)
        return now
      default:
        now.setDate(now.getDate() - 30)
        return now
    }
  }

  const exportLogs = () => {
    const csvContent = [
      // CSV headers
      ['Timestamp', 'Event Type', 'Severity', 'User', 'Order', 'Message', 'IP Address'].join(','),
      
      // CSV data
      ...logs.map(log => [
        new Date(log.created_at).toISOString(),
        log.event_type,
        log.severity,
        log.secret_users ? `${log.secret_users.first_name} ${log.secret_users.last_name}` : '',
        log.secret_orders?.order_number || '',
        `"${log.message.replace(/"/g, '""')}"`,
        log.ip_address || ''
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        type='success'
        title='Export Complete'
        message='Audit logs exported successfully'
      />
    ))
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="text-red-500" size={16} />
      case 'error':
        return <AlertTriangle className="text-red-400" size={16} />
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={16} />
      case 'info':
      default:
        return <Info className="text-blue-500" size={16} />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-red-500 bg-red-500/10'
      case 'error':
        return 'border-red-400 bg-red-400/10'
      case 'warning':
        return 'border-yellow-500 bg-yellow-500/10'
      case 'info':
      default:
        return 'border-blue-500 bg-blue-500/10'
    }
  }

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case 'transfer_created':
      case 'transfer_claimed':
      case 'transfer_cancelled':
        return <Share2 size={16} />
      case 'order_status_change':
        return <Package size={16} />
      case 'admin_action':
        return <Settings size={16} />
      case 'payment_event':
        return <CreditCard size={16} />
      case 'security_event':
        return <Shield size={16} />
      default:
        return <Info size={16} />
    }
  }

  const toggleLogExpansion = (logId: string) => {
    const newExpanded = new Set(expandedLogs)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
    }
    setExpandedLogs(newExpanded)
  }

  const formatMetadata = (metadata: any) => {
    if (!metadata || typeof metadata !== 'object') return null
    
    return Object.entries(metadata).map(([key, value]) => (
      <div key={key} className="text-xs">
        <span className="text-secret-text-secondary">{key}:</span>{' '}
        <span className="text-white">{JSON.stringify(value)}</span>
      </div>
    ))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Audit Logs</h2>
          <p className="text-secret-text-secondary">
            Security events, transfers, and system activity
          </p>
        </div>
        
        <button
          onClick={exportLogs}
          className="secret-btn-secondary flex items-center space-x-2"
        >
          <Download size={16} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-secret-bg-secondary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-secret-text-secondary text-sm">Total Events</div>
                <div className="text-white text-2xl font-bold">{stats.totalEvents}</div>
              </div>
              <Shield className="text-secret-primary" size={32} />
            </div>
          </div>

          <div className="bg-secret-bg-secondary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-secret-text-secondary text-sm">Critical Events</div>
                <div className="text-red-500 text-2xl font-bold">
                  {stats.eventsBySeverity.critical || 0}
                </div>
              </div>
              <XCircle className="text-red-500" size={32} />
            </div>
          </div>

          <div className="bg-secret-bg-secondary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-secret-text-secondary text-sm">Transfers</div>
                <div className="text-purple-500 text-2xl font-bold">
                  {(stats.eventsByType.transfer_created || 0) + 
                   (stats.eventsByType.transfer_claimed || 0)}
                </div>
              </div>
              <Share2 className="text-purple-500" size={32} />
            </div>
          </div>

          <div className="bg-secret-bg-secondary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-secret-text-secondary text-sm">Admin Actions</div>
                <div className="text-blue-500 text-2xl font-bold">
                  {stats.eventsByType.admin_action || 0}
                </div>
              </div>
              <Settings className="text-blue-500" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-secret-bg-secondary rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secret-text-secondary" size={16} />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="secret-input pl-12"
            />
          </div>

          {/* Event Type Filter */}
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="secret-select"
          >
            <option value="all">All Events</option>
            <option value="transfer_created">Transfer Created</option>
            <option value="transfer_claimed">Transfer Claimed</option>
            <option value="transfer_cancelled">Transfer Cancelled</option>
            <option value="order_status_change">Order Status</option>
            <option value="admin_action">Admin Actions</option>
            <option value="security_event">Security Events</option>
            <option value="payment_event">Payment Events</option>
          </select>

          {/* Severity Filter */}
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="secret-select"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="secret-select"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>

          {/* Results Count */}
          <div className="flex items-center text-secret-text-secondary">
            <Filter size={16} className="mr-2" />
            <span>{logs.length} events</span>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-secret-bg-secondary rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="text-center py-16">
            <Shield size={48} className="text-secret-text-tertiary mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Audit Logs</h3>
            <p className="text-secret-text-secondary">
              No events found matching your filters.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-secret-border">
            {logs.map((log) => (
              <div key={log.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {/* Event Icon */}
                    <div className={`p-2 rounded-lg border ${getSeverityColor(log.severity)}`}>
                      {getEventTypeIcon(log.event_type)}
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-white font-medium capitalize">
                          {log.event_type.replace(/_/g, ' ')}
                        </span>
                        {getSeverityIcon(log.severity)}
                        <span className={`text-xs px-2 py-1 rounded-full border ${getSeverityColor(log.severity)}`}>
                          {log.severity.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="text-white text-sm mb-2">
                        {log.message}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-xs text-secret-text-secondary">
                        <div className="flex items-center space-x-1">
                          <Clock size={12} />
                          <span>{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        
                        {log.secret_users && (
                          <div className="flex items-center space-x-1">
                            <User size={12} />
                            <span>
                              {log.secret_users.first_name} {log.secret_users.last_name}
                            </span>
                          </div>
                        )}
                        
                        {log.secret_orders && (
                          <div className="flex items-center space-x-1">
                            <Package size={12} />
                            <span>#{log.secret_orders.order_number}</span>
                          </div>
                        )}
                        
                        {log.ip_address && (
                          <span>IP: {log.ip_address}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expand Button */}
                  <button
                    onClick={() => toggleLogExpansion(log.id)}
                    className="text-secret-text-secondary hover:text-white p-1"
                  >
                    {expandedLogs.has(log.id) ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                </div>

                {/* Expanded Details */}
                {expandedLogs.has(log.id) && (
                  <div className="mt-4 pt-4 border-t border-secret-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {/* Basic Details */}
                      <div>
                        <h4 className="font-medium text-white mb-2">Event Details</h4>
                        <div className="space-y-1">
                          <div>
                            <span className="text-secret-text-secondary">Event ID:</span>{' '}
                            <span className="text-white font-mono text-xs">{log.id}</span>
                          </div>
                          {log.transfer_token && (
                            <div>
                              <span className="text-secret-text-secondary">Transfer Token:</span>{' '}
                              <span className="text-white font-mono text-xs">
                                {log.transfer_token.substring(0, 16)}...
                              </span>
                            </div>
                          )}
                          {log.old_value && log.new_value && (
                            <div>
                              <span className="text-secret-text-secondary">Change:</span>{' '}
                              <span className="text-red-400">{log.old_value}</span>
                              {' → '}
                              <span className="text-green-400">{log.new_value}</span>
                            </div>
                          )}
                          {log.user_agent && (
                            <div>
                              <span className="text-secret-text-secondary">User Agent:</span>{' '}
                              <span className="text-white text-xs">{log.user_agent.substring(0, 50)}...</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Metadata */}
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div>
                          <h4 className="font-medium text-white mb-2">Additional Data</h4>
                          <div className="bg-secret-bg-tertiary rounded p-2 space-y-1">
                            {formatMetadata(log.metadata)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More */}
      {logs.length >= (filter.limit || 50) && (
        <div className="text-center">
          <button
            onClick={() => {
              setFilter(prev => ({
                ...prev,
                limit: (prev.limit || 50) + 50
              }))
            }}
            className="secret-btn-secondary"
          >
            Load More Events
          </button>
        </div>
      )}
    </div>
  )
}

export default AuditLogs